import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getRoleDisplayName, type OldRole as Role } from "../../domain";
import EditIcon from "@/assets/icons/edit.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { PermissionGuard } from "@/components/PermissionGuard";
import {
  classicTableOuterClass,
  classicTableScrollClass,
  classicStickyTableHeaderClass,
} from "@/components/layout/tableLayout";

interface RolesTableProps {
  roles: Role[];
  onViewPermissions: (role: Role) => void;
  onViewMembers: (role: Role) => void;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canViewMember?: boolean;
}

const getAccessLevelLabel = (role: Role, t: TFunction): string => {
  const { total_permissions, granted_permissions } = role;
  if (granted_permissions == null || total_permissions == null) {
    return role.access_level ?? "-";
  }
  if (granted_permissions >= total_permissions) {
    return t("table.allPermissions");
  }
  return t("table.permissionsCount", { count: granted_permissions });
};

export const RolesTable: React.FC<RolesTableProps> = ({
  roles,
  onViewPermissions,
  onViewMembers,
  onEdit,
  onDelete,
  canView = true,
  canEdit = true,
  canDelete = true,
  canViewMember = true,
}) => {
  const { t } = useTranslation("rolesPermissions");
  const handleRowClick = (role: Role) => {
    if (!canView) return;
    onViewPermissions(role);
  };

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="mt-4">
      <div className="space-y-3 md:hidden">
        {roles.map((role) => (
          <div
            key={role.name}
            className={`rounded-xl border bg-white p-4 ${
              canView ? "cursor-pointer transition-colors hover:bg-gray-50" : ""
            }`}
            onClick={() => handleRowClick(role)}
            onKeyDown={(event) => {
              if (!canView) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleRowClick(role);
              }
            }}
            role={canView ? "button" : undefined}
            tabIndex={canView ? 0 : undefined}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">
                  {getRoleDisplayName(role)}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {getAccessLevelLabel(role, t)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <PermissionGuard allowed={canView}>
                  <button
                    type="button"
                    className="app-text-button"
                    onClick={(event) => {
                      stopRowClick(event);
                      onViewPermissions(role);
                    }}
                  >
                    {t("table.view")}
                  </button>
                </PermissionGuard>
                <PermissionGuard allowed={canEdit}>
                  <button
                    type="button"
                    onClick={(event) => {
                      stopRowClick(event);
                      onEdit(role);
                    }}
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                </PermissionGuard>
                <PermissionGuard allowed={canDelete}>
                  <button
                    type="button"
                    onClick={(event) => {
                      stopRowClick(event);
                      onDelete(role);
                    }}
                  >
                    <DeleteIcon className="h-4 w-4" />
                  </button>
                </PermissionGuard>
              </div>
            </div>
            <p
              className={canViewMember ? "app-text-button mt-4" : "mt-4 text-sm text-gray-600"}
              onClick={(event) => {
                if (!canViewMember) return;
                event.stopPropagation();
                onViewMembers(role);
              }}
              onKeyDown={(event) => {
                if (!canViewMember) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  onViewMembers(role);
                }
              }}
              role={canViewMember ? "button" : undefined}
              tabIndex={canViewMember ? 0 : undefined}
            >
              {t("table.members", { count: role.members_count })}
            </p>
          </div>
        ))}
      </div>

      <div className={classicTableOuterClass}>
        <div className={classicTableScrollClass}>
        <Table className="w-full min-w-[500px]">
          <TableHeader className={classicStickyTableHeaderClass}>
            <TableRow className="bg-[#D8DDE6] hover:bg-[#D8DDE6]">
            <TableHead className="py-3 px-4">{t("table.nameCol")}</TableHead>
            <TableHead className="py-3 px-4">{t("table.accessLevelCol")}</TableHead>
            <TableHead className="py-3 px-4">{t("table.membersCol")}</TableHead>
            <TableHead className="flex justify-end items-center py-3 px-4">
              {t("table.actionsCol")}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {roles.map((role) => (
            <TableRow
              key={role.name}
              onClick={() => handleRowClick(role)}
              className={
                canView ? "cursor-pointer hover:bg-gray-50" : undefined
              }
            >
              <TableCell className="py-3 px-4">{getRoleDisplayName(role)}</TableCell>
              <TableCell className="py-3 px-4">
                {getAccessLevelLabel(role, t)}
              </TableCell>
              <TableCell className="py-3 px-4" onClick={stopRowClick}>
                <button
                  type="button"
                  onClick={() => onViewMembers(role)}
                  disabled={!canViewMember}
                  className="app-text-button disabled:cursor-default"
                >
                  {role.members_count}
                </button>
              </TableCell>
              <TableCell
                className="flex items-center justify-end gap-3 py-3 px-4"
                onClick={stopRowClick}
              >
                <PermissionGuard allowed={canView}>
                  <button
                    type="button"
                    className="app-text-button"
                    onClick={() => onViewPermissions(role)}
                  >
                    {t("table.view")}
                  </button>
                </PermissionGuard>
                <PermissionGuard allowed={canEdit}>
                  <EditIcon
                    className="h-4 w-4 cursor-pointer"
                    onClick={() => onEdit(role)}
                  />
                </PermissionGuard>
                <PermissionGuard allowed={canDelete}>
                  <DeleteIcon
                    className="h-4 w-4 cursor-pointer"
                    onClick={() => onDelete(role)}
                  />
                </PermissionGuard>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
};
